import React, { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * SAML SSO Configuration Panel — mounted from Identity Console per practice_group.
 *
 * One SSO trust = one practice_group. Config includes the IdP details, an
 * "anchor practice" (default landing within the group when no dept mapping
 * applies), and optional dept→practice mapping for shared-tenant groups.
 */
function SamlConfigPanel({ practiceGroupId, groupName }) {
  const [config, setConfig] = useState(null);
  const [groupPractices, setGroupPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [deptMappings, setDeptMappings] = useState([]);
  const [deptForm, setDeptForm] = useState({ athena_dept_id: '', nh_practice_id: '' });
  const [bulkCsv, setBulkCsv] = useState('');
  const [bulkReplace, setBulkReplace] = useState(false);
  const [deptBusy, setDeptBusy] = useState(false);
  const [pgMappings, setPgMappings] = useState([]);
  const [pgForm, setPgForm] = useState({ athena_practice_id: '', nh_practice_group_id: '', default_nh_practice_id: '' });
  const [pgBulkCsv, setPgBulkCsv] = useState('');
  const [pgBulkReplace, setPgBulkReplace] = useState(false);
  const [pgBusy, setPgBusy] = useState(false);
  const [allGroups, setAllGroups] = useState([]);

  const [form, setForm] = useState({
    anchor_practice_id: '',
    idp_entity_id: '',
    idp_sso_url: '',
    idp_certificate: '',
    idp_metadata_url: '',
    sp_entity_id: window.location.origin,
    attr_email: 'email',
    attr_first_name: 'firstName',
    attr_last_name: 'lastName',
    attr_external_id: 'subject',
    attr_department: 'departmentid',
    default_role: 'care_provider',
    auto_provision_users: false,
    is_enabled: false,
  });

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await api.request(`/admin/saml/config/group/${practiceGroupId}`);
      if (res.success) {
        setGroupPractices(res.practices || []);
        if (res.data) {
          setConfig(res.data);
          setForm({
            anchor_practice_id: res.data.practice_id || '',
            idp_entity_id: res.data.idp_entity_id || '',
            idp_sso_url: res.data.idp_sso_url || '',
            idp_certificate: res.data.idp_certificate || '',
            idp_metadata_url: res.data.idp_metadata_url || '',
            sp_entity_id: res.data.sp_entity_id || window.location.origin,
            attr_email: res.data.attr_email || 'email',
            attr_first_name: res.data.attr_first_name || 'firstName',
            attr_last_name: res.data.attr_last_name || 'lastName',
            attr_external_id: res.data.attr_external_id || 'subject',
            attr_department: res.data.attr_department || 'departmentid',
            default_role: res.data.default_role || 'care_provider',
            auto_provision_users: !!res.data.auto_provision_users,
            is_enabled: !!res.data.is_enabled,
          });
          setExpanded(true);
        } else {
          // No config yet — default anchor to first practice in group
          const firstEnabled = (res.practices || []).find(p => p.enabled);
          if (firstEnabled) {
            setForm(f => ({ ...f, anchor_practice_id: firstEnabled.id }));
          }
        }
      }
    } catch {
      // No config exists yet — that's fine
    } finally {
      setLoading(false);
    }
  };

  const loadDeptMappings = async () => {
    try {
      const res = await api.request(`/admin/saml/config/group/${practiceGroupId}/departments`);
      if (res.success) setDeptMappings(res.data || []);
    } catch { /* silent */ }
  };

  const loadPgMappings = async () => {
    try {
      const res = await api.request(`/admin/saml/config/group/${practiceGroupId}/practices`);
      if (res.success) setPgMappings(res.data || []);
    } catch { /* silent */ }
  };

  const loadAllGroups = async () => {
    try {
      const res = await api.request('/admin/saml/configs');
      if (res.success) {
        setAllGroups((res.data || []).map(r => ({
          id: r.practice_group_id,
          name: r.group_name,
          practices: r.practices || [],
        })));
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadConfig();
    loadDeptMappings();
    loadPgMappings();
    loadAllGroups();
  }, [practiceGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    if (!form.idp_entity_id || !form.idp_sso_url || !form.idp_certificate) {
      setError('IdP Entity ID, SSO URL, and Certificate are required');
      return;
    }
    if (!form.anchor_practice_id) {
      setError('Anchor practice (default landing) is required');
      return;
    }
    setSaving(true);
    try {
      const res = await api.request(`/admin/saml/config/group/${practiceGroupId}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      if (res.success) {
        setConfig(res.data);
        setSuccess('SAML SSO configuration saved');
        setTimeout(() => setSuccess(null), 3000);
        await loadDeptMappings();
      } else {
        setError(res.error || 'Failed to save');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove SAML SSO configuration for group "${groupName}"? Users in all practices under this group will no longer be able to sign in via SSO.`)) return;
    try {
      await api.request(`/admin/saml/config/group/${practiceGroupId}`, { method: 'DELETE' });
      setConfig(null);
      setDeptMappings([]);
      setForm({
        anchor_practice_id: (groupPractices.find(p => p.enabled) || {}).id || '',
        idp_entity_id: '', idp_sso_url: '', idp_certificate: '', idp_metadata_url: '',
        sp_entity_id: window.location.origin,
        attr_email: 'email', attr_first_name: 'firstName', attr_last_name: 'lastName',
        attr_external_id: 'subject', attr_department: 'departmentid',
        default_role: 'care_provider', auto_provision_users: false, is_enabled: false,
      });
      setExpanded(false);
      setSuccess('SAML configuration removed');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddDept = async () => {
    setError(null);
    setSuccess(null);
    if (!deptForm.athena_dept_id || !deptForm.nh_practice_id) {
      setError('Athena dept ID and NH practice are required');
      return;
    }
    setDeptBusy(true);
    try {
      const res = await api.request(`/admin/saml/config/group/${practiceGroupId}/departments`, {
        method: 'POST',
        body: JSON.stringify({
          athena_dept_id: String(deptForm.athena_dept_id).trim(),
          nh_practice_id: parseInt(deptForm.nh_practice_id, 10),
        }),
      });
      if (res.success) {
        setDeptForm({ athena_dept_id: '', nh_practice_id: '' });
        await loadDeptMappings();
        setSuccess('Department mapping saved');
        setTimeout(() => setSuccess(null), 2000);
      } else {
        setError(res.error || 'Failed to save mapping');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDeptBusy(false);
    }
  };

  const handleDeleteDept = async (deptId) => {
    if (!window.confirm(`Remove mapping for department ${deptId}?`)) return;
    setDeptBusy(true);
    try {
      await api.request(`/admin/saml/config/group/${practiceGroupId}/departments/${encodeURIComponent(deptId)}`, { method: 'DELETE' });
      await loadDeptMappings();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeptBusy(false);
    }
  };

  const handleBulkImport = async () => {
    setError(null);
    setSuccess(null);
    if (!bulkCsv.trim()) {
      setError('Paste CSV lines like "21,5" first');
      return;
    }
    if (bulkReplace && !window.confirm('Replace ALL existing department mappings for this group? This cannot be undone.')) return;
    setDeptBusy(true);
    try {
      const res = await api.request(`/admin/saml/config/group/${practiceGroupId}/departments/bulk`, {
        method: 'POST',
        body: JSON.stringify({ csv: bulkCsv, replace: bulkReplace }),
      });
      if (res.success) {
        setBulkCsv('');
        setBulkReplace(false);
        await loadDeptMappings();
        setSuccess(`Imported ${res.imported} mapping(s)`);
        setTimeout(() => setSuccess(null), 3000);
      } else if (res.errors) {
        setError(res.error + '\n' + res.errors.join('\n'));
      } else {
        setError(res.error || 'Bulk import failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDeptBusy(false);
    }
  };

  const handleAddPg = async () => {
    setError(null); setSuccess(null);
    if (!pgForm.athena_practice_id || !pgForm.nh_practice_group_id || !pgForm.default_nh_practice_id) {
      setError('Athena practice ID, NH group, and default practice are all required');
      return;
    }
    setPgBusy(true);
    try {
      const res = await api.request(`/admin/saml/config/group/${practiceGroupId}/practices`, {
        method: 'POST',
        body: JSON.stringify({
          athena_practice_id: String(pgForm.athena_practice_id).trim(),
          nh_practice_group_id: parseInt(pgForm.nh_practice_group_id, 10),
          default_nh_practice_id: parseInt(pgForm.default_nh_practice_id, 10),
        }),
      });
      if (res.success) {
        setPgForm({ athena_practice_id: '', nh_practice_group_id: '', default_nh_practice_id: '' });
        await loadPgMappings();
        setSuccess('Athena practice mapping saved');
        setTimeout(() => setSuccess(null), 2000);
      } else {
        setError(res.error || 'Failed to save mapping');
      }
    } catch (err) { setError(err.message); } finally { setPgBusy(false); }
  };

  const handleDeletePg = async (athenaPracticeId) => {
    if (!window.confirm(`Remove mapping for Athena practice ${athenaPracticeId}?`)) return;
    setPgBusy(true);
    try {
      await api.request(`/admin/saml/config/group/${practiceGroupId}/practices/${encodeURIComponent(athenaPracticeId)}`, { method: 'DELETE' });
      await loadPgMappings();
    } catch (err) { setError(err.message); } finally { setPgBusy(false); }
  };

  const handleBulkImportPg = async () => {
    setError(null); setSuccess(null);
    if (!pgBulkCsv.trim()) {
      setError('Paste CSV lines like "15881,4,5" first');
      return;
    }
    if (pgBulkReplace && !window.confirm('Replace ALL existing Athena practice mappings for this config? This cannot be undone.')) return;
    setPgBusy(true);
    try {
      const res = await api.request(`/admin/saml/config/group/${practiceGroupId}/practices/bulk`, {
        method: 'POST',
        body: JSON.stringify({ csv: pgBulkCsv, replace: pgBulkReplace }),
      });
      if (res.success) {
        setPgBulkCsv('');
        setPgBulkReplace(false);
        await loadPgMappings();
        setSuccess(`Imported ${res.imported} mapping(s)`);
        setTimeout(() => setSuccess(null), 3000);
      } else if (res.errors) {
        setError(res.error + '\n' + res.errors.join('\n'));
      } else {
        setError(res.error || 'Bulk import failed');
      }
    } catch (err) { setError(err.message); } finally { setPgBusy(false); }
  };

  const copyMetadataUrl = () => {
    const url = `${window.location.origin}/api/auth/saml/metadata`;
    navigator.clipboard.writeText(url);
    setSuccess('SP Metadata URL copied to clipboard');
    setTimeout(() => setSuccess(null), 2000);
  };

  const isMultiPractice = groupPractices.filter(p => p.enabled).length > 1;

  if (loading) return <div style={{ padding: 16, color: '#9ca3af' }}>Loading SSO config...</div>;

  return (
    <div style={s.container}>
      <div style={s.header} onClick={() => setExpanded(!expanded)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🔐</span>
          <div>
            <strong style={{ fontSize: 14 }}>SAML SSO</strong>
            <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>
              {config ? (config.is_enabled ? 'Enabled' : 'Disabled') : 'Not configured'}
            </span>
          </div>
        </div>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>{expanded ? '▼' : '▶'}</span>
      </div>

      {expanded && (
        <div style={s.body}>
          {error && <div style={s.error}>{error}</div>}
          {success && <div style={s.success}>{success}</div>}

          {/* SP Info (our side) */}
          <div style={s.section}>
            <h4 style={s.sectionTitle}>Service Provider (Our Side)</h4>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
              Provide these to the EHR admin when configuring their SAML application.
            </p>
            <div style={s.row}>
              <label style={s.label}>SP Entity ID</label>
              <input style={s.input} value={form.sp_entity_id} onChange={e => setForm({ ...form, sp_entity_id: e.target.value })} />
            </div>
            <div style={s.row}>
              <label style={s.label}>ACS URL (Callback)</label>
              <input style={s.inputReadonly} readOnly value={`${window.location.origin}/api/auth/saml/callback`} />
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Shared across all groups — multi-tenant routing resolves from the SAML Response&apos;s IdP Entity ID.
              </p>
            </div>
            <button style={s.btnSecondary} onClick={copyMetadataUrl}>Copy SP Metadata URL</button>
          </div>

          {/* IdP Config (their side) */}
          <div style={s.section}>
            <h4 style={s.sectionTitle}>Identity Provider (EHR Side)</h4>
            <div style={s.row}>
              <label style={s.label}>IdP Entity ID *</label>
              <input style={s.input} placeholder="e.g., https://www.okta.com/exk..." value={form.idp_entity_id} onChange={e => setForm({ ...form, idp_entity_id: e.target.value })} />
            </div>
            <div style={s.row}>
              <label style={s.label}>IdP SSO URL *</label>
              <input style={s.input} placeholder="e.g., https://identity.athenahealth.com/app/.../sso/saml" value={form.idp_sso_url} onChange={e => setForm({ ...form, idp_sso_url: e.target.value })} />
            </div>
            <div style={s.row}>
              <label style={s.label}>IdP Certificate (PEM) *</label>
              <textarea style={{ ...s.input, height: 100, fontFamily: 'monospace', fontSize: 11 }} placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDp...&#10;-----END CERTIFICATE-----" value={form.idp_certificate} onChange={e => setForm({ ...form, idp_certificate: e.target.value })} />
            </div>
            <div style={s.row}>
              <label style={s.label}>IdP Metadata URL (optional)</label>
              <input style={s.input} placeholder="Auto-refresh certificate from metadata" value={form.idp_metadata_url} onChange={e => setForm({ ...form, idp_metadata_url: e.target.value })} />
            </div>
          </div>

          {/* Attribute Mapping */}
          <div style={s.section}>
            <h4 style={s.sectionTitle}>Attribute Mapping</h4>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
              Map SAML assertion attribute names to user fields.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                ['attr_email', 'Email', 'email'],
                ['attr_first_name', 'First Name', 'firstName'],
                ['attr_last_name', 'Last Name', 'lastName'],
                ['attr_external_id', 'External User ID', 'subject'],
                ['attr_department', 'Department', 'departmentid'],
              ].map(([key, label, placeholder]) => (
                <div key={key} style={s.row}>
                  <label style={s.label}>{label}</label>
                  <input style={s.input} placeholder={placeholder} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} />
                </div>
              ))}
            </div>
          </div>

          {/* Settings */}
          <div style={s.section}>
            <h4 style={s.sectionTitle}>Settings</h4>
            <div style={s.row}>
              <label style={s.label}>Default Landing Practice (Anchor) *</label>
              <select style={s.input} value={form.anchor_practice_id} onChange={e => setForm({ ...form, anchor_practice_id: parseInt(e.target.value, 10) })}>
                <option value="">Select practice...</option>
                {groupPractices.filter(p => p.enabled).map(p => (
                  <option key={p.id} value={p.id}>{p.name.trim()} (id={p.id})</option>
                ))}
              </select>
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                {isMultiPractice
                  ? 'This is the default practice users land in when their SAML departmentid doesn\'t match any mapping below. Use Department Mapping to route different depts to different practices.'
                  : 'Users will always land in this practice — no department mapping needed for single-practice groups.'}
              </p>
            </div>
            <div style={s.row}>
              <label style={s.label}>Default Role for New SSO Users</label>
              <select style={s.input} value={form.default_role} onChange={e => setForm({ ...form, default_role: e.target.value })}>
                <option value="care_provider">Care Provider</option>
                <option value="clinical_admin">Clinical Admin</option>
                <option value="practice_admin">Practice Admin</option>
              </select>
            </div>
            <label style={s.checkLabel}>
              <input type="checkbox" checked={form.auto_provision_users} onChange={e => setForm({ ...form, auto_provision_users: e.target.checked })} />
              Auto-create user accounts on first SSO login
            </label>
            <label style={s.checkLabel}>
              <input type="checkbox" checked={form.is_enabled} onChange={e => setForm({ ...form, is_enabled: e.target.checked })} />
              Enable SAML SSO for this group
            </label>
          </div>

          {/* Athena Practice Mapping (shared-IdP routing — one Okta app serving multiple Athena practices) */}
          {config && (
            <div style={s.section}>
              <h4 style={s.sectionTitle}>Athena Practice Mapping</h4>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                Route SSO users to NH practice groups by Athena&apos;s <code>practiceid</code> attribute. Use this when the same Okta app is configured to serve multiple Athena practices (e.g., 15881 + 26909). Leave empty to route all users to this group only. Once ANY mapping is added, users whose <code>practiceid</code> is not in the list will be rejected.
              </p>

              {pgMappings.length > 0 && (
                <table style={s.deptTable}>
                  <thead>
                    <tr>
                      <th style={s.deptTh}>Athena Practice ID</th>
                      <th style={s.deptTh}>NH Group</th>
                      <th style={s.deptTh}>Default Landing Practice</th>
                      <th style={s.deptTh}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pgMappings.map(m => (
                      <tr key={m.id}>
                        <td style={s.deptTd}><code>{m.athena_practice_id}</code></td>
                        <td style={s.deptTd}>{m.nh_practice_group_name} (id={m.nh_practice_group_id})</td>
                        <td style={s.deptTd}>{m.default_nh_practice_name.trim()} (id={m.default_nh_practice_id})</td>
                        <td style={{ ...s.deptTd, textAlign: 'right' }}>
                          <button style={s.btnTiny} onClick={() => handleDeletePg(m.athena_practice_id)} disabled={pgBusy}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12, marginBottom: 16 }}>
                <div style={{ flex: '0 0 130px' }}>
                  <label style={s.label}>Athena Practice ID</label>
                  <input style={s.input} placeholder="e.g., 15881" value={pgForm.athena_practice_id} onChange={e => setPgForm({ ...pgForm, athena_practice_id: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Route to NH Group</label>
                  <select
                    style={s.input}
                    value={pgForm.nh_practice_group_id}
                    onChange={e => setPgForm({ ...pgForm, nh_practice_group_id: e.target.value, default_nh_practice_id: '' })}
                  >
                    <option value="">Select group...</option>
                    {allGroups.map(g => (
                      <option key={g.id} value={g.id}>{g.name} (id={g.id})</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Default Landing Practice</label>
                  <select
                    style={s.input}
                    value={pgForm.default_nh_practice_id}
                    onChange={e => setPgForm({ ...pgForm, default_nh_practice_id: e.target.value })}
                    disabled={!pgForm.nh_practice_group_id}
                  >
                    <option value="">Select practice...</option>
                    {allGroups.find(g => String(g.id) === String(pgForm.nh_practice_group_id))?.practices?.filter(p => p.enabled).map(p => (
                      <option key={p.id} value={p.id}>{p.name.trim()} (id={p.id})</option>
                    ))}
                  </select>
                </div>
                <button style={s.btnSecondary} onClick={handleAddPg} disabled={pgBusy}>Add</button>
              </div>

              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#334155', fontWeight: 500 }}>Bulk import from CSV</summary>
                <p style={{ fontSize: 11, color: '#94a3b8', margin: '8px 0' }}>
                  One line per mapping: <code>athena_practice_id,nh_practice_group_id,default_nh_practice_id</code>. Example: <code>15881,4,5</code> routes Athena practice 15881 to NH group 4, defaulting to NH practice 5.
                </p>
                <textarea
                  style={{ ...s.input, height: 100, fontFamily: 'monospace', fontSize: 12 }}
                  placeholder={'15881,4,5\n26909,3,4'}
                  value={pgBulkCsv}
                  onChange={e => setPgBulkCsv(e.target.value)}
                />
                <label style={s.checkLabel}>
                  <input type="checkbox" checked={pgBulkReplace} onChange={e => setPgBulkReplace(e.target.checked)} />
                  Replace all existing mappings (otherwise merge)
                </label>
                <button style={{ ...s.btnSecondary, marginTop: 4 }} onClick={handleBulkImportPg} disabled={pgBusy}>Import</button>
              </details>
            </div>
          )}

          {/* Department Mapping (Option C — for Athena tenants that serve multiple NH sub-brands) */}
          {config && isMultiPractice && (
            <div style={s.section}>
              <h4 style={s.sectionTitle}>Department Mapping</h4>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                Route SSO users to specific practices within <strong>{groupName}</strong> by Athena&apos;s <code>departmentid</code> attribute. Leave empty to route all users to the anchor practice. Once ANY mapping is added, users whose <code>departmentid</code> is not in the list will be rejected.
              </p>

              {/* Existing mappings table */}
              {deptMappings.length > 0 && (
                <table style={s.deptTable}>
                  <thead>
                    <tr>
                      <th style={s.deptTh}>Athena Dept ID</th>
                      <th style={s.deptTh}>NH Practice</th>
                      <th style={s.deptTh}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptMappings.map(m => (
                      <tr key={m.id}>
                        <td style={s.deptTd}><code>{m.athena_dept_id}</code></td>
                        <td style={s.deptTd}>{m.nh_practice_name} (id={m.nh_practice_id})</td>
                        <td style={{ ...s.deptTd, textAlign: 'right' }}>
                          <button style={s.btnTiny} onClick={() => handleDeleteDept(m.athena_dept_id)} disabled={deptBusy}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Add single mapping form */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12, marginBottom: 16 }}>
                <div style={{ flex: '0 0 140px' }}>
                  <label style={s.label}>Athena Dept ID</label>
                  <input style={s.input} placeholder="e.g., 21" value={deptForm.athena_dept_id} onChange={e => setDeptForm({ ...deptForm, athena_dept_id: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Route to NH Practice</label>
                  <select style={s.input} value={deptForm.nh_practice_id} onChange={e => setDeptForm({ ...deptForm, nh_practice_id: e.target.value })}>
                    <option value="">Select practice...</option>
                    {groupPractices.filter(p => p.enabled).map(p => (
                      <option key={p.id} value={p.id}>{p.name.trim()} (id={p.id})</option>
                    ))}
                  </select>
                </div>
                <button style={s.btnSecondary} onClick={handleAddDept} disabled={deptBusy}>Add</button>
              </div>

              {/* Bulk CSV paste */}
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#334155', fontWeight: 500 }}>Bulk import from CSV</summary>
                <p style={{ fontSize: 11, color: '#94a3b8', margin: '8px 0' }}>
                  One line per mapping: <code>athena_dept_id,nh_practice_id</code>. Example: <code>21,5</code> means Athena dept 21 routes to NH practice 5.
                </p>
                <textarea
                  style={{ ...s.input, height: 120, fontFamily: 'monospace', fontSize: 12 }}
                  placeholder={'21,5\n22,5\n27,6'}
                  value={bulkCsv}
                  onChange={e => setBulkCsv(e.target.value)}
                />
                <label style={s.checkLabel}>
                  <input type="checkbox" checked={bulkReplace} onChange={e => setBulkReplace(e.target.checked)} />
                  Replace all existing mappings (otherwise merge)
                </label>
                <button style={{ ...s.btnSecondary, marginTop: 4 }} onClick={handleBulkImport} disabled={deptBusy}>Import</button>
              </details>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            {config && (
              <button style={s.btnDanger} onClick={handleDelete}>Remove SSO</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  container: { border: '1px solid #e2e8f0', borderRadius: 10, marginTop: 16, overflow: 'hidden' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', background: '#f8fafc', cursor: 'pointer',
    borderBottom: '1px solid #e2e8f0',
  },
  body: { padding: 16 },
  section: { marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f1f5f9' },
  sectionTitle: { fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 },
  row: { marginBottom: 8 },
  label: { display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 3 },
  input: {
    width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: 13, color: '#1e293b', boxSizing: 'border-box',
  },
  inputReadonly: {
    width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6,
    fontSize: 12, color: '#64748b', background: '#f8fafc', boxSizing: 'border-box', fontFamily: 'monospace',
  },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', marginBottom: 6 },
  btnPrimary: {
    padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none',
    borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  btnSecondary: {
    padding: '6px 14px', background: '#f1f5f9', color: '#334155', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 12, cursor: 'pointer', marginTop: 4,
  },
  btnDanger: {
    padding: '8px 20px', background: '#fff', color: '#dc2626', border: '1px solid #fca5a5',
    borderRadius: 6, fontSize: 13, cursor: 'pointer',
  },
  btnTiny: {
    padding: '3px 10px', background: '#fff', color: '#dc2626', border: '1px solid #fca5a5',
    borderRadius: 4, fontSize: 11, cursor: 'pointer',
  },
  deptTable: {
    width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 4,
  },
  deptTh: {
    textAlign: 'left', padding: '6px 10px', background: '#f8fafc',
    color: '#475569', fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: 0.4, borderBottom: '1px solid #e2e8f0',
  },
  deptTd: { padding: '6px 10px', color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
  error: { padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#dc2626', marginBottom: 12 },
  success: { padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 12, color: '#16a34a', marginBottom: 12 },
};

export default SamlConfigPanel;
